/**
 * Newsletter Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorType,
  ColorVariant,
  GradientDirection,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseColorFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatColorFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatResponsiveValue,
} from '../../format-utils'
import newsletterTemplate from './newsletter.template.json'

export interface INewsletterComponentSettings {
  // Data settings
  'newsletter-data-source': string
  'newsletter-data-email': string
  'newsletter-data-comp-title': VariableTextValue
  'newsletter-data-placeholder': VariableTextValue
  'newsletter-data-button-text': VariableTextValue
  'newsletter-data-icon': string

  // Design settings - Button Color
  'newsletter-design-button-color': ColorType
  'newsletter-design-button-color-solid': ColorVariant
  'newsletter-design-button-color-start-gradient': ColorVariant
  'newsletter-design-button-color-end-gradient': ColorVariant
  'newsletter-design-button-color-gradient-direction': GradientDirection
  
  // Design settings - Title Color
  'newsletter-design-comp-title-color': ColorType
  'newsletter-design-comp-title-color-solid': ColorVariant
  'newsletter-design-comp-title-color-start-gradient': ColorVariant
  'newsletter-design-comp-title-color-end-gradient': ColorVariant
  'newsletter-design-comp-title-color-gradient-direction': GradientDirection
  
  // Design settings - Other
  'newsletter-design-border-color': ColorVariant
  'newsletter-design-size': string
  'newsletter-design-icon-width': string[] | number[]
  'newsletter-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'newsletter-design-alignment': string
  'newsletter-design-comp-title-alignment': string
  'newsletter-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'newsletter-design-comp-title-margin': SpacingInputValue
  'newsletter-design-padding': SpacingInputValue
  'newsletter-design-typography': {
    'newsletter-design-component-title-typography': ResponsiveValue<TypographyValue>
    'newsletter-design-input-typography': ResponsiveValue<TypographyValue>
    'newsletter-design-button-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'newsletter-action-on-click-subscribe': BuilderActions
}

export interface INewsletterComponent extends IBuilderComponent {
  componentId: 'newsletter'
  groupId: 'app'
  settings: INewsletterComponentSettings
}

export interface AiNewsletterInput extends BaseAiComponentInput {
  newsletterTitle?: string[] // Format: ["text:Newsletter Title"] or ["text:Subscribe to ", "cr:newsletterName"]
  placeholder?: string // Email input placeholder
  buttonText?: string // Subscribe button text
  icon?: string // Icon name or URL
  size?: string // e.g., "small", "medium", "large"
  width?: string // Format: "w:100%" or "w:600px" or "w:100% de:w:600px"
  margin?: string // Format: "m:10px" or "mt:10px mb:10px"
  padding?: string // Format: "p:16px"
  alignment?: string // Format: "center" or "left ta:center"
  titleAlignment?: string // Format: "center" or "left"
  borderColor?: string // e.g., "primary-90"
  buttonColor?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right" or "adaptive"
  titleColor?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right" or "adaptive"
  typography?: string // Format: "body-medium" or "body-medium ta:body-large"
  onSubscribeAction?: BuilderActions[]
}

const NEWSLETTER_DEFAULT_TEMPLATE = newsletterTemplate as unknown as INewsletterComponentSettings

export class NewsletterComp {
  getTemplate(): INewsletterComponentSettings {
    return NEWSLETTER_DEFAULT_TEMPLATE
  }

  /**
   * format newsletter component settings to AI newsletter input
   */
  format(settings: INewsletterComponentSettings): AiNewsletterInput {
    const result: AiNewsletterInput = {
      type: 'newsletter',
    }

    // Data - Component Title
    if (settings['newsletter-data-comp-title']) {
      result.newsletterTitle = formatFromVariableText(settings['newsletter-data-comp-title'])
    }

    // Data - Placeholder
    if (settings['newsletter-data-placeholder']) {
      const placeholder = formatFromVariableText(settings['newsletter-data-placeholder'])
      result.placeholder = placeholder.length > 0 ? placeholder.join(' ') : undefined
    }

    // Data - Button Text
    if (settings['newsletter-data-button-text']) {
      const buttonText = formatFromVariableText(settings['newsletter-data-button-text'])
      result.buttonText = buttonText.length > 0 ? buttonText.join(' ') : undefined
    }

    // Data - Icon
    if (settings['newsletter-data-icon']) {
      result.icon = settings['newsletter-data-icon']
    }

    // Design - Size
    if (settings['newsletter-design-size']) {
      result.size = settings['newsletter-design-size']
    }

    // Design - Width (handle OverflowSettings)
    if (settings['newsletter-design-width']) {
      const width = formatWidthFormat(settings['newsletter-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Margin (handle OverflowSettings)
    if (settings['newsletter-design-margin']) {
      const margin = formatSpacingFormat(settings['newsletter-design-margin'])
      if (margin) {
        result.margin = margin
      }
    }

    // Design - Padding
    if (settings['newsletter-design-padding']) {
      result.padding = formatSpacingFormat({ phone: settings['newsletter-design-padding'] } as any)
    }

    // Design - Alignment
    if (settings['newsletter-design-alignment']) {
      result.alignment = formatResponsiveValue({ phone: settings['newsletter-design-alignment'] } as any, (val) => String(val))
    }

    // Design - Title Alignment
    if (settings['newsletter-design-comp-title-alignment']) {
      result.titleAlignment = settings['newsletter-design-comp-title-alignment']
    }

    // Design - Border Color
    if (settings['newsletter-design-border-color']) {
      result.borderColor = settings['newsletter-design-border-color']
    }

    // Design - Button Color
    if (settings['newsletter-design-button-color']) {
      result.buttonColor = formatColorFormat({
        colorType: { light: settings['newsletter-design-button-color'], dark: settings['newsletter-design-button-color'] },
        colorSolid: { light: settings['newsletter-design-button-color-solid'], dark: settings['newsletter-design-button-color-solid'] },
        colorStartGradient: { light: settings['newsletter-design-button-color-start-gradient'], dark: settings['newsletter-design-button-color-start-gradient'] },
        colorEndGradient: { light: settings['newsletter-design-button-color-end-gradient'], dark: settings['newsletter-design-button-color-end-gradient'] },
        colorGradientDirection: { light: settings['newsletter-design-button-color-gradient-direction'], dark: settings['newsletter-design-button-color-gradient-direction'] },
      })
    }

    // Design - Title Color
    if (settings['newsletter-design-comp-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: { light: settings['newsletter-design-comp-title-color'], dark: settings['newsletter-design-comp-title-color'] },
        colorSolid: { light: settings['newsletter-design-comp-title-color-solid'], dark: settings['newsletter-design-comp-title-color-solid'] },
        colorStartGradient: { light: settings['newsletter-design-comp-title-color-start-gradient'], dark: settings['newsletter-design-comp-title-color-start-gradient'] },
        colorEndGradient: { light: settings['newsletter-design-comp-title-color-end-gradient'], dark: settings['newsletter-design-comp-title-color-end-gradient'] },
        colorGradientDirection: { light: settings['newsletter-design-comp-title-color-gradient-direction'], dark: settings['newsletter-design-comp-title-color-gradient-direction'] },
      })
    }

    // Design - Typography (nested structure)
    if (settings['newsletter-design-typography']) {
      const titleTypo = formatTypographyFormat(settings['newsletter-design-typography']['newsletter-design-component-title-typography'])
      const inputTypo = formatTypographyFormat(settings['newsletter-design-typography']['newsletter-design-input-typography'])
      const buttonTypo = formatTypographyFormat(settings['newsletter-design-typography']['newsletter-design-button-typography'])
      // If all are the same, use single typography field
      if (titleTypo === inputTypo && inputTypo === buttonTypo) {
        result.typography = titleTypo
      } else {
        // For now, use title typography as default
        result.typography = titleTypo
      }
    }

    return result
  }

  /**
   * parse AI newsletter input to newsletter component settings
   */
  parse(input: AiNewsletterInput): INewsletterComponentSettings {
    const result = deepMerge(NEWSLETTER_DEFAULT_TEMPLATE, {})

    // Data - Component Title
    if (input.newsletterTitle) {
      result['newsletter-data-comp-title'] = parseToVariableText(input.newsletterTitle)
    }

    // Data - Placeholder
    if (input.placeholder) {
      result['newsletter-data-placeholder'] = parseToVariableText(input.placeholder)
    }

    // Data - Button Text
    if (input.buttonText) {
      result['newsletter-data-button-text'] = parseToVariableText(input.buttonText)
    }

    // Data - Icon
    if (input.icon) {
      result['newsletter-data-icon'] = input.icon
    }

    // Design - Size
    if (input.size) {
      result['newsletter-design-size'] = input.size
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['newsletter-design-width'] = {
        ...widthParsed,
        overflowSetting: result['newsletter-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['newsletter-design-margin'] = {
        ...marginParsed,
        overflowSetting: result['newsletter-design-margin'].overflowSetting,
      }
    }

    // Design - Padding
    if (input.padding) {
      result['newsletter-design-padding'] = parseSpacingFormat(input.padding).phone
    }

    // Design - Alignment
    if (input.alignment) {
      result['newsletter-design-alignment'] = parseResponsiveFormat(input.alignment, (v) => v).phone
    }

    // Design - Title Alignment
    if (input.titleAlignment) {
      result['newsletter-design-comp-title-alignment'] = input.titleAlignment
    }

    // Design - Border Color
    if (input.borderColor) {
      result['newsletter-design-border-color'] = input.borderColor as ColorVariant
    }

    // Design - Button Color (with gradient support)
    // Newsletter doesn't support light/dark theme variations, so extract just the light value
    if (input.buttonColor) {
      const colorParsed = parseColorFormat(input.buttonColor)
      result['newsletter-design-button-color'] = colorParsed.colorType.light
      result['newsletter-design-button-color-solid'] = colorParsed.colorSolid.light
      result['newsletter-design-button-color-start-gradient'] = colorParsed.colorStartGradient.light
      result['newsletter-design-button-color-end-gradient'] = colorParsed.colorEndGradient.light
      result['newsletter-design-button-color-gradient-direction'] = colorParsed.colorGradientDirection.light
    }

    // Design - Title Color (with gradient support)
    // Newsletter doesn't support light/dark theme variations, so extract just the light value
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['newsletter-design-comp-title-color'] = colorParsed.colorType.light
      result['newsletter-design-comp-title-color-solid'] = colorParsed.colorSolid.light
      result['newsletter-design-comp-title-color-start-gradient'] = colorParsed.colorStartGradient.light
      result['newsletter-design-comp-title-color-end-gradient'] = colorParsed.colorEndGradient.light
      result['newsletter-design-comp-title-color-gradient-direction'] = colorParsed.colorGradientDirection.light
    }

    // Design - Typography (nested structure)
    if (input.typography) {
      const parsedTypography = parseTypographyFormat(input.typography)
      result['newsletter-design-typography']['newsletter-design-component-title-typography'] = parsedTypography
      result['newsletter-design-typography']['newsletter-design-input-typography'] = parsedTypography
      result['newsletter-design-typography']['newsletter-design-button-typography'] = parsedTypography
    }

    // Actions - Subscribe
    if (input.onSubscribeAction) {
      // @ts-ignore
      result['newsletter-action-on-click-subscribe'] = input.onSubscribeAction
    }

    return result
  }
}
